<template>
	<Application
		v-model:errorTitle="errorTitle"
		:errorDetail="errorDetail"
		v-model:successTitle="successTitle"
		:successDetail="successDetail"
		:loading="loading"
		:external="true"
	>
		<iframe
			ref="iframe"
			:class="iFrameClass"
		></iframe>
		<v-dialog v-model="tradeHubClientDialog" max-width="420px" persistent>
			<v-card>
				<v-card-title>Trade Hub</v-card-title>
				<v-card-text>
					<v-select
						v-model="selectedTradeHubClientId"
						:items="tradeHubClientOptions"
						item-title="label"
						item-value="id"
						variant="outlined"
						density="compact"
						hide-details
					/>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn variant="text" @click="cancelTradeHubClientSelection">{{ $t('text.cancel') }}</v-btn>
					<v-btn color="primary" variant="flat" :disabled="!selectedTradeHubClientId" @click="confirmTradeHubClientSelection">{{ $t('text.select') }}</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</Application>
</template>

<script>
// TODO: the default bahaviour of vuetify is to allow scrolling on the html element
//       but with plugins this creates an awkward elastic-scroll-effect
//       we should style: html { overflow-y: hidden !important; }
//       unfortunately ALWAYS setting it will break other MYS apps..
//       how to only do it when a plugin app is active?
//       maybe the Application comp can set a class on the html element?

import Loading from 'vue-loading-overlay'
import Alert from '@/components/common/Alert.vue'
import Mys from './MYS.js'
import Application from '../Application.vue'
import { version } from '../../../../package.json'
import eventBus from '@/utils/eventBus.js'
import { UserType } from '../../../../../shared/types/account-types'
import { getAppLinkExtraSegments, stripAppLinkPrefix, resolveAppByLink } from './deepLinkPath.js'
import { getShareTokenFromHash, toShareTokenHash, savePendingShareToken, getPendingShareToken, clearPendingShareToken } from './tradeHubShareLink.js'
import { checkImpersonationContext } from './impersonationContext.js'

export default {
	name: 'AppHost',
	components: { Loading, Alert, Application },
	props: {
		locale: String,
		_getUser: Function,
		_getIdentity: Function,
		_userHasAppAccess: Function,
		client: Object,
		selectedComponent: Object, // optionally the container can pass in the application object
	},
	data: () => ({
		loading: false,
		errorTitle: '',
		errorDetail: '',
		successTitle: '',
		successDetail: '',
		messageListener: null,
		mys: null,
		app: null,
		appPath: null,
		fullPath: '',
		url: '',
		iFrameClass: null,
		tradeHubTokenPayload: null,
		tradeHubClientDialog: false,
		tradeHubClientOptions: [],
		selectedTradeHubClientId: null,
		selectedTradeHubClient: null,
		tradeHubClientSelectionResolver: null,
		tradeHubClientSelectionRejecter: null,
		// Trade Hub share token this load was opened with (see tradeHubShareLink.js)
		tradeHubShareToken: null,
		leavingLockedTradeHub: false,
		// Trade Hub query params that can arrive as real query params on a fresh load -- most from
		// a payment-provider redirect, `shareToken` from a trip share link opened cold (see
		// mounted()) -- deliberately an allowlist so an unrelated query param on this outer
		// MyServices URL (e.g. serviceProviderId) never gets misread as deep-link content.
		DEEPLINK_QUERY_KEYS: ['segmentId', 'requestId', 'openRequest', 'openTopUp', 'walletOpen', 'walletManageCards', 'walletAddMoney', 'hasError', 'alias', 'paymentSuccess', 'paymentCurrency', 'paymentKind', 'shareToken'],
		// Query params a payment provider appends to the redirect URL itself (never read by any
		// app -- confirmed unused in both this repo and trade-hub-app) that would otherwise
		// linger on this outer MyServices URL forever, since they're neither part of the
		// recognized deep-link content above nor cleaned up by anything else afterward.
		PROVIDER_ARTIFACT_QUERY_KEYS: ['datatransTrxId'],
	}),
	watch: {
		locale(v) {
			// TODO: only send events in after the plugin has initialized
			this.mys?.localeChanged(v)
		},
	},
	async mounted() {
		console.log('AppHost mounted')
		this.appPath = this.$route.params.appName
		this.app = this.selectedComponent ?? this.$store.state.selectedComponent

		// The app can be null if the plugin is accessed via a deeplink. Resolve it BEFORE
		// computing the deep-link hash below (moved up from further down in this method) --
		// we need the app's own canonical `link` value to tell a genuine deep-link sub-path
		// apart from a stale trailing segment that's already part of the app's own link
		// (see MYS-7185: billing-manager's link was '/app/billingManager/billing', so its
		// own root wrongly looked like a deep link to '/billing' once MYS-6945 started
		// treating wildcard sub-paths as deep-link signals).
		if (!this.app?.fields) {
			const allApplications = this.allApplications ?? this.$store.state.allApplications ?? []
			// Match on just the app-root path (/app/<appName>), not the full window.location.pathname --
			// a wildcard sub-path deep link (e.g. /app/trade-hub/trip/<id>) would never exact-match a
			// Contentful app's root `link` field, leaving `this.app`/`this.url` unresolved (this route is
			// only ever AppHost.vue, never the standalone /sa-app/... routes, so no /sa-app/ handling needed).
			this.app = resolveAppByLink(allApplications, `/app/${this.appPath}`)
			console.log('AppHost auto-selected app', this.app)
		}

		// Segments of this app's OWN link beyond its `/app/:appPath` root (e.g. ['billing']
		// for billing-manager's stale link, [] for trade-hub-app's plain '/app/trade-hub').
		const appLinkExtraSegments = getAppLinkExtraSegments(this.app, this.appPath)

		// Check if URL has a deeplink hash value
		this.fullPath = this.$route.fullPath

		let deeplinkHash = ''
		const urlHashIndex = this.fullPath.indexOf('/#/')
		if (urlHashIndex > -1) {
			deeplinkHash = this.fullPath.substring(urlHashIndex + 3, this.fullPath.length)
		}
		else {
			// Strip the app's own extra link segments off the matched wildcard sub-path FIRST --
			// only what's left over (if anything) is a genuine deep-link candidate, not just the
			// tail of the app's own (possibly stale) link.
			const genuinePath = stripAppLinkPrefix(this.$route.params.path, appLinkExtraSegments)

			if (genuinePath || this.DEEPLINK_QUERY_KEYS.some(key => key in this.$route.query)) {
				// No /#/ fragment, but there's a genuine wildcard sub-path (/app/:appName/:path(.*))
				// and/or a recognized query param -- this is a fresh load from a payment-provider
				// redirect (successUrl/errorUrl/cancelUrl), which can only carry state as real query
				// params on this outer MyServices URL: URL fragments are never transmitted over HTTP,
				// so they don't survive that redirect (confirmed live for trade-hub-app's Datatrans
				// flow -- only Datatrans's own `?datatransTrxId=...` query param comes through).
				// Reconstruct the equivalent deep-link hash from the matched path + recognized query.
				// No leading slash here -- matches the existing convention (currentRouteHash() below
				// never includes one either), since open() below already supplies the separating slash
				// via `/#/${hash}`; a leading slash here would double up into `/#//...`.
				const relevantQuery = Object.fromEntries(
					Object.entries(this.$route.query).filter(([key]) => this.DEEPLINK_QUERY_KEYS.includes(key))
				)
				const search = new URLSearchParams(relevantQuery).toString()
				deeplinkHash = `${genuinePath}${search ? `?${search}` : ''}`

				// Both the wildcard sub-path (e.g. /trip/<id>) and these recognized query keys were only
				// ever a one-time signal for this initial load -- reset THIS outer MyServices route back
				// to just the app root now, before open()/setHash() below build the /#/ hash on top of it.
				// Otherwise they'd stay baked into the base path forever (setHash() preserves everything
				// before the first /#/ verbatim), duplicated once for real and once inside the hash, and
				// lingering as dead, meaningless path/query even once the wallet dialog closes.
				// Provider artifact keys (e.g. datatransTrxId) are dropped here too -- unlike the
				// recognized deep-link keys above, they were never meant to be preserved anywhere at
				// all, just appended by the payment provider on top of whatever URL we gave it.
				const remainingQuery = Object.fromEntries(
					Object.entries(this.$route.query).filter(
						([key]) => !this.DEEPLINK_QUERY_KEYS.includes(key) && !this.PROVIDER_ARTIFACT_QUERY_KEYS.includes(key)
					)
				)
				await this.$router.replace({ path: `/app/${this.appPath}`, query: remainingQuery }).catch(() => {})
			}
			else {
				// The persisted hash is child-app session state. A fresh dashboard tile
				// open without an explicit /#/ deeplink must start from the app root.
				this.$store.commit('setDeeplinkHash', '')
			}
		}

		// test app: 'https://admin.skiline.cc/php/tools/proto-mys-plugin/2/TestApp.html'
		this.url = this.app?.fields?.appConfig?.de?.url
		if (this.isTradeHubApp()) {
			// MYS-7449: a plain Trade Hub open resumes a share link that was parked while Trade Hub
			// was still locked (see leaveLockedTradeHub()) -- an explicit deep link always wins.
			if (!deeplinkHash) {
				const pendingShareToken = getPendingShareToken(localStorage, this.$store.state.loggedInUser?.sys?.id)
				if (pendingShareToken) deeplinkHash = toShareTokenHash(pendingShareToken)
			}
			this.tradeHubShareToken = getShareTokenFromHash(deeplinkHash)

			const userId = this.$store.state.loggedInUser?.sys?.id
			const serviceProvider = this.$store.state.isImpersonation
				? this.$store.state.selectedServiceProvider
				: this.$store.state.loggedInUser?.fields?.serviceProvider?.de
			const serviceProviderId = serviceProvider?.sys?.id ?? this.$route.query?.serviceProviderId
			if (userId && serviceProviderId) {
				this.$store.commit('setTradeHubVisitedForServiceProvider', { userId, serviceProviderId, visited: true })
			}
		}

		// TODO: how to use the local instance instead of the url configured in the toolbox app?
		// LOCAL DEV OVERRIDE: Use local trade-hub app for testing
		// Remove this block when done testing
		// if (this.url?.includes('trade-hub')) {
		// 	console.log('AppHost: Overriding trade-hub URL to local dev server')
		// 	this.url = 'http://localhost:5173'
		// }

		console.log('AppHost url', this.url)
		if (!this.url && this.$store.state.loggedInUser?.fields) throw new Error('App url not found in config!')

		this.mys = new Mys(this.url, this, this.$refs.iframe.contentWindow)

		// TODO: bootstrap: how to auth the app itself?
		//       a s2s call?
		//       api key is insecure since it would be visible in the client
		//       host -(challenge)-> guest -(challenge)-> guest server -> -(solution)-> mys server
		//                                   <-(solution)-'
		//       can a pure browser-app be authenticated safely at all?
		// TODO: limit guest urls - how?
		// TODO: require auth with every call + signature verification (both sides)
		// TODO: pass down location parameters and appPath?
		this.open(this.url, deeplinkHash)
	},
	beforeUnmount() {
		this.mys?.destroy()
	},
	methods: {
		open(url, hash) {
			if (hash?.length) {
				url = `${url}/#/${hash}`
				this.setHash(hash)
			}

			const config = this.getAppConfig()
			if (config?.target) {
				window.open(url, '_blank')
				this.$emit('show-dashboard')
				return
			}

			this.$refs.iframe.src = url
		},
		// mys api implementation
		async getClient(param) {
			if (!this.selectedTradeHubClient && this.isTradeHubApp()) {
				await this.getTouroperatorToken()
			}
			return this.selectedTradeHubClient ?? this.client ?? this.$store.state.selectedClient
		},
		getClients(param) {
			if (this.client) return [this.client]
			return this.$store.state.loggedInUser?.fields?.clients?.de ?? []
		},
		async setClient(param) {
			const clients = this.getClients()

			/* param can be a :
				- MYS Client Sys Id e.g. 15Y3LzDkWo0RFsplUPu043
				- MYS Client object
				- MYS Client Id e.g. SGV
				- MYS Shop Client Id e.g. TUZ
			*/
			const selectedClient = clients.find(client =>
				client.sys.id === param ||
				client.sys.id === param?.sys?.id ||
				client?.fields?.clientId?.de === param ||
				client?.fields?.shopClientId?.de === param
			);

			if (!selectedClient) {
				throw new Error("Client selection is invalid for logged in user");
			}

			await this.$store.commit('setSelectedClient', selectedClient)
		},
		getAppComponent(param) {
			return this.app
		},
		getAppPathName(param) {
			return this.$route.params.appName
		},
		getCurrentURL() {
			return window.location.href
		},
		isTradeHubApp() {
			const linkPath = this.app?.fields?.link?.de ?? ''
			const isTradeHubRootRoute = this.appPath === 'trade-hub' && !this.$route.params?.path
			const isTradeHubRootLink = /\/trade-hub\/?$/.test(linkPath)
			return isTradeHubRootRoute || isTradeHubRootLink
		},
		getQuery(param) {
			return window.location.search
		},
		getIdentity(param) {
			if (this._getIdentity) return this._getIdentity(param)

			return this.$store.state.loggedInUser.kc_token
		},
		getUser(param) {
			if (this._getUser) return this._getUser(param)

			if (this.$store.state.isImpersonation) {
				return this.$store.state.impersonatedServiceProvider.fields.mainUserAccount
			}
			return this.$store.state.loggedInUser
		},
		getLocale(param) {
			return this.locale ?? this.$store.state.selectedLocale
		},
		getLocales(param) {
			return this.$store.state.locales
		},
		getHash(param) {
			return this.currentRouteHash() ?? this.$store.state.deeplinkHash ?? ''
		},
		async setHash(param) {
			await this.$store.commit('setDeeplinkHash', param);

			// Read the CURRENT route fresh -- this.fullPath is only a mount-time snapshot and never
			// updates, so re-using it here silently discarded whatever router.replace() below had
			// actually navigated to on every call after the first, letting the /#/ hash re-append
			// onto a stale (sometimes fragment-less) base instead of replacing it.
			let path = this.$route.fullPath

			//Reset URL to exclude Hash value if there is one in the URL already
			const urlHashIndex = path.indexOf('/#/')
			if (urlHashIndex > -1) {
				path = path.substring(0, urlHashIndex)
			}

			if (param?.length) {
				path = `${path}/#/${param}`
			}

			this.$router.replace(path).catch(err => { })
		},
		currentRouteHash() {
			const fullPath = this.$route.fullPath ?? ''
			const urlHashIndex = fullPath.indexOf('/#/')
			if (urlHashIndex < 0) return null

			// Keep the leading '#' -- this is the only caller-visible signal getHash() gives
			// trade-hub-app's authorizeUser() to tell "genuinely current route, trust it" apart from
			// "only the persisted deeplinkHash store fallback, possibly stale" (which stays #-free).
			// trade-hub-app's parseMysRoute(hash, allowRawPath=false) requires a literal '#' to accept
			// a hash as a real navigation target precisely to avoid restoring stale sessions -- see
			// tradehub-app commit ec0d6cf "Prevent stale MYS route restore".
			return fullPath.substring(urlHashIndex + 1, fullPath.length)
		},
		userHasAppAccess(param) {
			if (this._userHasAppAccess) return this._userHasAppAccess(param)

			let userApps = []
			const userType = this.$store.state.loggedInUser?.fields?.type?.de

			if (userType === UserType.OPERATOR) {
				if (this.$store.state.isImpersonation === false) {
					//Operator user is logged in and not impersonating an SP
					userApps = this.$store.state.loggedInUser?.fields?.applications?.de ?? []
				}
				else {
					// operator user impersonating a sp
					userApps = this.$store.state.impersonatedServiceProvider?.fields?.userAccount?.de?.fields?.applications?.de ?? []
				}
			}
			else if (userType === UserType.TRADE_HUB) {
				userApps = this.$store.state.loggedInUser?.fields?.applications?.de ?? []
			}
			else {
				//User is a service provider
				userApps = this.$store.state.selectedServiceProvider?.fields?.userAccount?.de?.fields?.applications?.de ?? []
			}

			return userApps.findIndex(userApp => userApp.sys.id === this.app.sys.id) > -1
		},
		// MYS-7544: the impersonation context is persisted across sessions, so it is checked before use.
		// One minted for another login is a leftover - the impersonation is dropped, not sent (the api
		// would refuse it). An expired one of this login is renewed (it lives 30 minutes only).
		async getImpersonationContextToken() {
			const token = this.$store.state.impersonationContextToken
			if (!token) throw new Error('Failed to create touroperator token: missing impersonation context')

			const context = checkImpersonationContext(token, this.$store.state.loggedInUser?.kc_token)
			if (context.state === 'foreign') {
				console.warn('[AppHost] dropping an impersonation context of another login')
				// same cleanup as exitImpersonation(), minus its /exit-impersonation call and dashboard switch
				const loggedInUser = this.$store.state.loggedInUser
				await this.$store.commit('resetImpersonation')
				if (loggedInUser?.fields?.type?.de === UserType.SERVICE_PROVIDER) {
					await this.$store.commit('setSelectedServiceProvider', loggedInUser.fields.serviceProvider?.de)
				}
				else {
					await this.$store.commit('setSelectedServiceProvider', null)
					await this.$store.commit('setPeakProducts', null)
				}
				await this.$store.commit('setComponents', loggedInUser?.fields?.applications?.de)
				return null
			}

			if (context.state === 'valid') return token

			const serviceProviderId = context.serviceProviderId ?? this.$store.state.impersonatedServiceProvider?.sys?.id
			const res = await this.$httpGet(`/serviceprovider/${serviceProviderId}?startImpersonation=true`)
			if (!res?.impersonationContextToken) throw new Error('Failed to create touroperator token: could not renew impersonation context')
			await this.$store.commit('setImpersonationContextToken', res.impersonationContextToken)
			return res.impersonationContextToken
		},
		async getTouroperatorToken(clientIdOverride = null) {
			const options = {}
			if (clientIdOverride) {
				options.clientId = clientIdOverride
			}
			if (this.$store.state.isImpersonation === true) {
				const impersonationToken = await this.getImpersonationContextToken()
				if (impersonationToken) options.impersonationToken = impersonationToken
			}

			let res
			try {
				res = await this.$httpGet('/tradehub-token', {}, options)
			}
			catch (error) {
				if (error?.code === 'TRADEHUB_CLIENT_SELECTION_REQUIRED' && error?.clients?.length) {
					const selectedClientId = await this.openTradeHubClientSelection(error.clients)
					return await this.getTouroperatorToken(selectedClientId)
				}
				if (error?.code === 'TRADEHUB_NOT_UNLOCKED') {
					await this.leaveLockedTradeHub()
				}
				throw error
			}
			if (!res?.token) throw new Error('Failed to create touroperator token')
			// Trade Hub opens with the share link now and keeps it pending on its own side from here
			if (this.tradeHubShareToken) clearPendingShareToken(localStorage)
			if (res.client) this.selectedTradeHubClient = this.toTradeHubClient(res.client)
			this.tradeHubTokenPayload = this.decodeJwtPayload(res.token)
			return res.token
		},
		// MYS-7449: the user has not unlocked Trade Hub yet (e.g. a Tour Operator who just registered
		// through a share link), so the guest can never initialize -- send them to the dashboard to
		// finish onboarding instead of leaving them on an empty iframe.
		async leaveLockedTradeHub() {
			if (this.leavingLockedTradeHub) return
			this.leavingLockedTradeHub = true

			if (this.tradeHubShareToken) {
				savePendingShareToken(localStorage, { token: this.tradeHubShareToken, userId: this.$store.state.loggedInUser?.sys?.id })
			}
			// OAuthCallback leaves the share link in initialUrl, and Dashboard would replay it straight back here
			await this.$store.commit('setInitialUrl', '')
			await this.$store.commit('setDeeplinkHash', '')
			eventBus.$emit('addToastMessage', this.$t('text.lockedTradeHubAppTooltip'), 'info')
			this.$router.replace('/dashboard').catch(() => {})
		},
		openTradeHubClientSelection(clients) {
			this.tradeHubClientOptions = clients.map(client => ({
				...client,
				label: client.title || client.clientId || client.id,
				client: this.toTradeHubClient(client),
			}))
			this.selectedTradeHubClientId = this.tradeHubClientOptions[0]?.id ?? null
			this.tradeHubClientDialog = true

			return new Promise((resolve, reject) => {
				this.tradeHubClientSelectionResolver = resolve
				this.tradeHubClientSelectionRejecter = reject
			})
		},
		confirmTradeHubClientSelection() {
			const selectedClientId = this.selectedTradeHubClientId
			this.selectedTradeHubClient = this.tradeHubClientOptions.find(client => client.id === selectedClientId)?.client ?? null
			this.tradeHubClientDialog = false
			this.tradeHubClientSelectionResolver?.(selectedClientId)
			this.tradeHubClientSelectionResolver = null
			this.tradeHubClientSelectionRejecter = null
		},
		cancelTradeHubClientSelection() {
			this.tradeHubClientDialog = false
			this.tradeHubClientSelectionRejecter?.(new Error('Trade Hub client selection cancelled'))
			this.tradeHubClientSelectionResolver = null
			this.tradeHubClientSelectionRejecter = null
		},
		toTradeHubClient(client) {
			const title = client.title || client.clientId || client.id
			return {
				sys: {
					id: client.id,
				},
				fields: {
					title: {
						de: title,
					},
					clientId: {
						de: client.clientId || client.id,
					},
					billingDomainId: {
						de: client.billingDomainId,
					},
					currency: {
						de: client.currency,
					},
				},
			}
		},
		decodeJwtPayload(token) {
			try {
				const payload = token?.split?.('.')?.[1]
				if (!payload) return null
				const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/')
				return JSON.parse(atob(normalizedPayload))
			}
			catch (error) {
				console.error('[AppHost] Failed to decode Trade Hub token payload', error)
				return null
			}
		},
		async getTradehubToken() {
			return await this.getTouroperatorToken()
		},
		getAppConfig() {
			return this.app?.fields?.appConfig?.de ?? null
		},
		navigateToUrl(href) {
			const url = new URL(href, window.location.href)
			if (url.protocol !== 'https:' && url.protocol !== 'http:') throw new Error('navigateToUrl only supports http(s) urls')
			window.location.href = url.href
		},
		async getUserType() {
			const payload = this.tradeHubTokenPayload ?? this.decodeJwtPayload(await this.getTradehubToken())

			if (payload?.tok) return { type: 'tourOperator' }
			if (payload?.spk) return { type: 'serviceProvider' }
			if (payload?.opd) return { type: 'operator' }

			throw new Error('Failed to resolve Trade Hub user type from token')
		},
		showError(param) {
			this.errorTitle = param.title
			this.errorDetail = param.detail
		},
		showSuccess(param) {
			this.successTitle = param.title
			this.successDetail = param.detail
		},
		setIframeClass(param) {
			const className = param.className
			const add = param.add
			const name = typeof className === 'string' ? className.trim() : ''
			if (!name) return

			const currentClasses = (this.iFrameClass ?? '').split(/\s+/).filter(Boolean)
			const hasClass = currentClasses.includes(name)
			const nextClasses = add
				? (hasClass ? currentClasses : [...currentClasses, name])
				: currentClasses.filter(item => item !== name)

			this.iFrameClass = nextClasses.join(' ')
		},
		getMysVersion() {
			return version
		},
		showToast(param) {
			const type = param.type ?? 'info'
			eventBus.$emit('addToastMessage', param.message, type)
		},
	}
}
</script>

<style scoped>
	iframe {
		position: absolute;
		left: 0;
		top: 0;
		width: 100%;
		height: 100%;
		border: 0;
	}

	.theme--light.v-app-bar.v-toolbar.v-sheet {
		background-color: transparent !important;
	}

	.navbar {
		background-color: transparent !important;
	}

	iframe.dialog-open {
		z-index: 2405;
	}
</style>
